import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"

const bareId = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value)

export function parsePythonMetricActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const a = action.args
  const invalid = {
    ok: false as const,
    problems: ["invalid Python metric draft, inputs or tested revision"],
  }
  if (action.kind === "save_python_analysis_metric") {
    if (
      !bareId(a.analysis_id) ||
      !bareId(a.simulation_output_id) ||
      typeof a.fingerprint !== "string" ||
      !/^[a-f0-9]{64}$/.test(a.fingerprint) ||
      typeof a.display_name !== "string" ||
      !a.display_name.trim() ||
      a.display_name.length > 200 ||
      typeof a.source !== "string" ||
      !a.source.trim() ||
      new TextEncoder().encode(a.source).length > 65536 ||
      (a.output_type !== "number" && a.output_type !== "boolean") ||
      (a.description !== undefined &&
        (typeof a.description !== "string" || a.description.length > 4000))
    ) {
      return invalid
    }
    return {
      ok: true,
      action: {
        kind: action.kind,
        args: {
          analysis_id: a.analysis_id,
          fingerprint: a.fingerprint,
          display_name: a.display_name,
          description: a.description,
          source: a.source,
          output_type: a.output_type,
          simulation_output_id: a.simulation_output_id,
        },
      },
    }
  }
  if (action.kind === "create_python_metric_draft") {
    const frames = a.frame_tables ?? []
    if (
      typeof a.display_name !== "string" ||
      !a.display_name.trim() ||
      a.display_name.length > 200 ||
      typeof a.source !== "string" ||
      !a.source.trim() ||
      new TextEncoder().encode(a.source).length > 65536 ||
      (a.output_type !== "number" && a.output_type !== "boolean") ||
      (a.description !== undefined &&
        (typeof a.description !== "string" || a.description.length > 4000)) ||
      (a.unit !== undefined &&
        (typeof a.unit !== "string" ||
          !a.unit.trim() ||
          a.unit.length > 64 ||
          a.output_type === "boolean")) ||
      (a.audio !== undefined && typeof a.audio !== "boolean") ||
      (a.traces !== undefined && typeof a.traces !== "boolean") ||
      !Array.isArray(frames) ||
      frames.length > 32 ||
      frames.some(
        (f) =>
          typeof f !== "string" || !/^[A-Za-z_][A-Za-z0-9_]{0,127}$/.test(f),
      ) ||
      new Set(frames).size !== frames.length ||
      ((a.target_metric_id !== undefined ||
        a.target_version_id !== undefined) &&
        (!bareId(a.target_metric_id) || !bareId(a.target_version_id)))
    ) {
      return invalid
    }
    return {
      ok: true,
      action: {
        kind: action.kind,
        args: {
          display_name: a.display_name,
          source: a.source,
          output_type: a.output_type,
          description: a.description,
          unit: a.unit,
          audio: a.audio === true,
          traces: a.traces === true,
          frame_tables: frames as string[],
          target_metric_id: a.target_metric_id,
          target_version_id: a.target_version_id,
        },
      },
    }
  }
  if (action.kind === "preview_python_metric") {
    if (!bareId(a.draft_id) || !bareId(a.simulation_output_id)) {
      return invalid
    }
    return {
      ok: true,
      action: {
        kind: action.kind,
        args: {
          draft_id: a.draft_id,
          simulation_output_id: a.simulation_output_id,
        },
      },
    }
  }
  if (action.kind === "cancel_python_metric_preview") {
    if (!bareId(a.draft_id) || !bareId(a.preview_id)) {
      return invalid
    }
    return {
      ok: true,
      action: {
        kind: action.kind,
        args: { draft_id: a.draft_id, preview_id: a.preview_id },
      },
    }
  }
  if (action.kind !== "publish_python_metric") {
    return undefined
  }
  if (
    !bareId(a.draft_id) ||
    typeof a.fingerprint !== "string" ||
    !/^[a-f0-9]{64}$/.test(a.fingerprint) ||
    !Array.isArray(a.preview_ids) ||
    a.preview_ids.length < 1 ||
    a.preview_ids.length > 10 ||
    !a.preview_ids.every(bareId) ||
    new Set(a.preview_ids).size !== a.preview_ids.length
  ) {
    return invalid
  }
  return {
    ok: true,
    action: {
      kind: action.kind,
      args: {
        draft_id: a.draft_id,
        fingerprint: a.fingerprint,
        preview_ids: a.preview_ids,
      },
    },
  }
}

export function pythonMetricActionTitle(
  action: SofiaProposedAction,
): string | undefined {
  if (action.kind === "create_python_metric_draft") {
    const name =
      typeof action.args.display_name === "string"
        ? action.args.display_name
        : ""
    return `Create Python draft: ${name}`
  }
  if (action.kind === "save_python_analysis_metric") {
    return `Save as metric: ${typeof action.args.display_name === "string" ? action.args.display_name : ""}`
  }
  if (action.kind === "preview_python_metric") {
    return "Test Python draft"
  }
  if (action.kind === "cancel_python_metric_preview") {
    return "Cancel Python preview"
  }
  if (action.kind === "publish_python_metric") {
    return "Publish tested Python metric"
  }
  return undefined
}
